/**
 * The one shape every page's header takes.
 *
 * Three pages grew this layout independently and a fourth copied it by hand,
 * comments and all; the other seven kept their title and their range selector
 * in the page body instead. The result read as several products: the range
 * control sat in a different place on nearly every screen, and on a board it
 * scrolled away while on a detail page it stayed. Encoding the shape once is
 * what makes "the same spot" true by construction rather than by review.
 *
 * The bar is rendered into the app header via `usePageHeader`, which lives
 * outside the scroll container -- see PageHeaderContext for why that matters.
 */
import { Fragment, type ReactNode } from "react";
import {
  type RangeInfo,
  RangeSelect,
  RangeWarning,
  rangeSpan,
} from "@/components/profitability/RangeSelect";
import { cn } from "@/lib/utils";

/**
 * One labelled fact beside the title.
 *
 * Labelled, always: the same account reads $667,339 on its own page and
 * $339,623 on the list, and a bare figure in a bar full of figures gives the
 * reader no way to tell which one they are quoting.
 */
export interface PageHeaderField {
  key: string;
  label: string;
  content: ReactNode;
  /** Caps what share of the bar the field can claim. Names need it, figures do not. */
  widthClass?: string;
}

/**
 * The money-and-verdict shape the company, account and project pages all show.
 * Exported so a fourth page cannot invent a fifth way of writing a figure.
 */
export function PageHeaderMetric({
  value,
  badge,
  note,
  noteTitle,
}: {
  value: ReactNode;
  badge?: ReactNode;
  /** A caveat that changes how the figure should be read, never decoration. */
  note?: string | null;
  noteTitle?: string;
}) {
  return (
    <>
      <div className="flex items-center gap-2">
        <span className="text-lg font-semibold tabular-nums">{value}</span>
        {badge}
      </div>
      {note ? (
        <span className="text-[11px] text-muted-foreground" title={noteTitle}>
          {note}
        </span>
      ) : null}
    </>
  );
}

export function PageHeaderBar({
  eyebrow,
  title,
  titleBadge,
  fields,
  filters,
  range,
  rangeValue,
  onRangeChange,
}: {
  /** A breadcrumb Link to the parent, or a plain label. Omitted on a top-level board. */
  eyebrow?: ReactNode;
  title: string;
  titleBadge?: ReactNode;
  fields?: (PageHeaderField | null | false)[];
  /** Page-specific controls -- search, Filters, contract type. Range is not one: this owns it. */
  filters?: ReactNode;
  range: RangeInfo | undefined;
  /**
   * Omit both on a board that has no range (This month is always the open
   * month): no range control renders, rather than one that changes nothing.
   */
  rangeValue?: string;
  onRangeChange?: (key: string) => void;
}) {
  const shown = (fields ?? []).filter((field): field is PageHeaderField => Boolean(field));

  return (
    <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-2">
      {/* NOT flex-1. Growing the identity block consumed all the slack in the
          row, which left ml-auto on the controls with nothing to push against:
          the filters ended up hard against the metric with a wide gap before
          the avatar. min-w-0 lets the name truncate; max-w caps how much of the
          bar a long name can claim, and a fixed basis instead of a cap cut
          names short at 1440 while 250px sat empty beside them. */}
      <div className="flex min-w-0 max-w-sm shrink flex-col justify-center leading-tight">
        {eyebrow}
        <div className="flex min-w-0 items-center gap-2">
          <h1 className="truncate font-display text-base font-semibold" title={title}>
            {title}
          </h1>
          {titleBadge}
        </div>
      </div>

      {shown.map((field) => (
        <Fragment key={field.key}>
          {/* A real divider, not a gap: name, money and badge are all semibold
              navy on one baseline and read as a single string without it. */}
          <div aria-hidden className="h-8 w-px shrink-0 bg-border" />
          <div
            className={cn(
              "flex min-w-0 shrink flex-col justify-center leading-tight",
              field.widthClass ?? "shrink-0",
            )}
          >
            <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
              {field.label}
            </span>
            {field.content}
          </div>
        </Fragment>
      ))}

      {/* Controls, grouped and pushed right. shrink-0 on the cluster is what
          keeps the theme toggle and avatar on screen -- at 1024 they were
          pushed to x=1170. items-start so the caption hangs under the select
          rather than centring the whole cluster against it. */}
      <div className="ml-auto flex shrink-0 items-start gap-2">
        {filters}
        {/* The span rides on the control as a tooltip rather than under it as a
            line of text. "September 2025 to August 2026 · closed periods only"
            restates a dropdown that already reads "Last 12 months", and ten
            pages of that is clutter. On hover it costs nothing. */}
        {rangeValue !== undefined && onRangeChange ? (
          <span
            title={
              range
                ? `${rangeSpan(range)}${range.closed_only ? " · closed periods only" : ""}`
                : undefined
            }
          >
            <RangeSelect
              range={range}
              value={rangeValue}
              onChange={onRangeChange}
              showCaption={false}
            />
          </span>
        ) : null}
      </div>

      {/* The caveat, and ONLY on a range that has one -- nothing renders on an
          ordinary range. This is the line that changes a reading rather than
          describing the control: 2026 closed through August is $8.9M against
          $9.5M for all of 2025, and without it that is a down year instead of
          eight months outpacing twelve. Full row at w-full so it wraps under
          the bar instead of squeezing the selects. */}
      {range ? (
        <div className="flex w-full justify-end empty:hidden">
          <RangeWarning range={range} />
        </div>
      ) : null}
    </div>
  );
}
